/*
================================================================================
FILE HEADER — apps/studio-web/src/components/ReportsPage.tsx
================================================================================

CHANGE LOG
----------
| #  | Date & Time      | Who               | Why                        | What Changed                              |
|----|------------------|-------------------|----------------------------|-------------------------------------------|
| 1  | 2026-06-25 00:00 | Claude Sonnet 4.6 | Execution History (Ph3)    | Created — basic run cards from db.runs    |
| 2  | 2026-07-02 00:00 | Claude Sonnet 4.6 | Mimiq Reports parity       | Full rewrite: flat table, all columns,    |
|    |                  |                   |                            | inline step expand, bulk actions, groups  |

FILES THAT CALL / IMPORT THIS FILE
------------------------------------
- apps/studio-web/src/App.tsx

FUNCTIONS / EXPORTS COVERED
----------------------------
- ReportsPage       — main page component with filter/sort/select state
- DisplayRow        — flattened type: one row per test within a run
- StepSubTable      — inline expanded step detail sub-table
- MoveToGroupModal  — modal for creating / selecting / removing groups

CALLER → CALLEE MAP
--------------------
- ReportsPage → db.runs (useLiveQuery), db.groups (useLiveQuery)
- ReportsPage → MoveToGroupModal
- ReportsPage → StepSubTable (when row expanded)
- ReportsPage → generateHTMLReport (for ↓ HTML download)
- MoveToGroupModal → db.groups.add / db.runs.update

IMPORTANT POINTS
----------------
1. The table is FLAT: each db.runs[i].tests[j] = one row. Runs are not
   shown as collapsible groups; individual test executions are the unit.
2. "Move to Group" operates on the parent DBRun (groupId on DBRun, not test).
   Deleting a selected row deletes the entire parent DBRun batch.
3. STATUS badge shows "✗ Failed (N healed)" when healCount > 0.
4. STEPS column shows "P/T" (passed/total). "XN" healed suffix added when > 0.
5. The ↓ HTML button generates and downloads an in-browser HTML report for
   that single test using generateHTMLReport() from report-gen.ts.
6. coords, hint, screenshotDataUrl in DBRunStep are populated in Phase 3+
   runner relay mode. Existing rows show "—" for those columns.

ISSUES FACED / KNOWN GOTCHAS
-----------------------------
- useLiveQuery for db.groups must guard against table not existing in old
  DB versions; handled by try/catch fallback to empty array.
================================================================================
*/

import { useState, useMemo, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, type DBRun, type DBRunStep, type DBRunTest, type DBRunGroup } from '../lib/db.js';
import { generateHTMLReport, type StudioRunResult } from '../ir/report-gen.js';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DisplayRow {
  key:         string;   // `${runId}__${testId}` unique key
  runId:       string;
  testId:      string;
  date:        number;
  testName:    string;
  tags:        string[];
  browser:     string;
  status:      'passed' | 'failed';
  passedSteps: number;
  totalSteps:  number;
  healCount:   number;
  durationMs:  number;
  groupId?:    string;
  steps:       DBRunStep[];
}

type SortCol = 'date' | 'name' | 'status' | 'steps' | 'duration';
type SortDir = 'asc' | 'desc';
type StatusFilter = 'all' | 'passed' | 'failed';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(ms: number) {
  return new Date(ms).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function fmtDuration(ms: number) {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function browserLabel(b?: string) {
  if (!b || b === 'chromium') return 'Chrome';
  if (b === 'firefox')        return 'Firefox';
  if (b === 'webkit')         return 'WebKit';
  return b;
}

function downloadHtml(filename: string, html: string) {
  const blob = new Blob([html], { type: 'text/html' });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement('a'), { href: url, download: filename });
  a.click();
  URL.revokeObjectURL(url);
}

function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement('a'), { href: url, download: filename });
  a.click();
  URL.revokeObjectURL(url);
}

// ── Column layout constants ───────────────────────────────────────────────────

const COL = '40px 130px 1fr 110px 72px 148px 72px 68px 64px';
const STEP_COL = '28px 78px 1fr 90px 100px 84px 1fr 80px';

// ── Step status badge ─────────────────────────────────────────────────────────

function StepBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    ok:        { bg: '#14532d', color: '#4ade80', label: 'OK' },
    not_found: { bg: '#7f1d1d', color: '#f87171', label: 'NOT FOUND' },
    error:     { bg: '#78350f', color: '#fbbf24', label: 'ERROR' },
    skipped:   { bg: '#27272a', color: '#71717a', label: 'SKIP' },
  };
  const s = map[status] ?? { bg: '#27272a', color: '#a1a1aa', label: status.toUpperCase() };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px', borderRadius: 4,
      background: s.bg, color: s.color, fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
    }}>{s.label}</span>
  );
}

// ── Status badge (test level) ─────────────────────────────────────────────────

function StatusBadge({ status, healCount }: { status: 'passed' | 'failed'; healCount: number }) {
  if (status === 'passed') {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px',
        borderRadius: 5, background: '#14532d33', color: '#4ade80', fontSize: 11, fontWeight: 700,
      }}>✓ Passed</span>
    );
  }
  const healSuffix = healCount > 0 ? ` (${healCount} healed)` : '';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px',
      borderRadius: 5, background: '#7f1d1d33', color: '#f87171', fontSize: 11, fontWeight: 700,
    }}>✗ Failed{healSuffix}</span>
  );
}

// ── Sort indicator ────────────────────────────────────────────────────────────

function SortIcon({ col, active, dir }: { col: string; active: boolean; dir: SortDir }) {
  if (!active) return <span style={{ color: '#3f3f46', fontSize: 9 }}>↕</span>;
  return <span style={{ color: '#a78bfa', fontSize: 9 }}>{dir === 'asc' ? '↑' : '↓'}</span>;
}

// ── Step sub-table (expanded inline) ─────────────────────────────────────────

function StepSubTable({ steps }: { steps: DBRunStep[] }) {
  const HDR: React.CSSProperties = {
    fontSize: 10, fontWeight: 700, color: '#52525b', letterSpacing: '0.06em',
    padding: '6px 8px', textTransform: 'uppercase',
  };
  const CELL: React.CSSProperties = {
    fontSize: 11, color: '#a1a1aa', padding: '5px 8px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };

  return (
    <div style={{ background: '#0a0a0d', borderTop: '1px solid #1c1c1f', padding: '0 0 4px 0' }}>
      {/* Sub-table header */}
      <div style={{
        display: 'grid', gridTemplateColumns: STEP_COL,
        borderBottom: '1px solid #1c1c1f',
      }}>
        <div style={HDR}>#</div>
        <div style={HDR}>Type</div>
        <div style={HDR}>Step Name</div>
        <div style={HDR}>Status</div>
        <div style={HDR}>Matched By</div>
        <div style={HDR}>Coords</div>
        <div style={HDR}>Detail / Hint</div>
        <div style={HDR}>Screenshot</div>
      </div>

      {/* Step rows */}
      {steps.map((step, idx) => (
        <div
          key={step.stepId}
          style={{
            display: 'grid', gridTemplateColumns: STEP_COL,
            borderBottom: '1px solid #111113',
            background: idx % 2 === 0 ? 'transparent' : '#0d0d10',
            alignItems: 'center',
          }}
        >
          <div style={{ ...CELL, color: '#3f3f46', textAlign: 'right' }}>{idx + 1}</div>
          <div style={{ ...CELL, color: '#71717a' }}>{step.stepType}</div>
          <div style={{ ...CELL, color: '#e4e4e7' }}>{step.label ?? '—'}</div>
          <div style={{ ...CELL }}>
            <StepBadge status={step.status} />
          </div>
          <div style={{ ...CELL, color: '#a78bfa' }}>{step.matchedBy ?? '—'}</div>
          <div style={{ ...CELL }}>{step.coords ?? '—'}</div>
          <div style={{ ...CELL, color: '#71717a', whiteSpace: 'normal', lineHeight: '1.3' }}>
            {step.hint ?? '—'}
          </div>
          <div style={{ ...CELL }}>
            {step.screenshotDataUrl ? (
              <img
                src={step.screenshotDataUrl}
                alt="step screenshot"
                style={{ width: 64, height: 40, objectFit: 'cover', borderRadius: 3, cursor: 'pointer' }}
                onClick={() => window.open(step.screenshotDataUrl)}
              />
            ) : '—'}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Move to Group Modal ───────────────────────────────────────────────────────

function MoveToGroupModal({
  selectedCount,
  groups,
  onConfirm,
  onCancel,
}: {
  selectedCount: number;
  groups: DBRunGroup[];
  onConfirm: (groupId: string | null, newGroupName?: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState('');
  const [newName, setNewName] = useState('');

  const isCreate  = value === '__create__';
  const isRemove  = value === '__remove__';
  const canSubmit = isRemove || (isCreate && newName.trim().length > 0) || (!isCreate && !isRemove && value !== '');

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#1c1c1f', border: '1px solid #3f3f46', borderRadius: 10,
        padding: '24px 28px', minWidth: 360, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#e4e4e7' }}>
          Move {selectedCount} run{selectedCount !== 1 ? 's' : ''} to group
        </h3>

        <select
          value={value}
          onChange={e => { setValue(e.target.value); setNewName(''); }}
          style={{
            width: '100%', background: '#0f0f11', border: '1px solid #3f3f46',
            borderRadius: 6, padding: '8px 10px', color: '#e4e4e7', fontSize: 13,
            marginBottom: 10,
          }}
        >
          <option value="">— select a group —</option>
          <option value="__remove__">Remove from group (ungrouped)</option>
          {groups.map(g => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
          <option value="__create__">+ Create new group…</option>
        </select>

        {isCreate && (
          <input
            autoFocus
            placeholder="Group name…"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && canSubmit && onConfirm(null, newName.trim())}
            style={{
              width: '100%', boxSizing: 'border-box',
              background: '#0f0f11', border: '1px solid #3f3f46',
              borderRadius: 6, padding: '8px 10px', color: '#e4e4e7', fontSize: 13,
              marginBottom: 10, outline: 'none',
            }}
          />
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button
            onClick={onCancel}
            style={{
              background: 'none', border: '1px solid #3f3f46', borderRadius: 6,
              padding: '7px 18px', color: '#a1a1aa', cursor: 'pointer', fontSize: 13,
            }}
          >Cancel</button>
          <button
            disabled={!canSubmit}
            onClick={() => {
              if (isRemove) { onConfirm(null); }
              else if (isCreate) { onConfirm(null, newName.trim()); }
              else { onConfirm(value); }
            }}
            style={{
              background: canSubmit ? '#7c3aed' : '#3f3f46',
              border: 'none', borderRadius: 6,
              padding: '7px 18px', color: canSubmit ? '#fff' : '#71717a',
              cursor: canSubmit ? 'pointer' : 'default', fontSize: 13, fontWeight: 600,
            }}
          >Move</button>
        </div>
      </div>
    </div>
  );
}

// ── ReportsPage ───────────────────────────────────────────────────────────────

export function ReportsPage() {
  const runs   = useLiveQuery(() => db.runs.orderBy('runAt').reverse().toArray(), []);
  const groups = useLiveQuery(() => db.groups.orderBy('createdAt').toArray(), []) ?? [];

  const [search,        setSearch]        = useState('');
  const [statusFilter,  setStatusFilter]  = useState<StatusFilter>('all');
  const [dateFrom,      setDateFrom]      = useState('');
  const [dateTo,        setDateTo]        = useState('');
  const [sortCol,       setSortCol]       = useState<SortCol>('date');
  const [sortDir,       setSortDir]       = useState<SortDir>('desc');
  const [selected,      setSelected]      = useState<Set<string>>(new Set());
  const [expanded,      setExpanded]      = useState<Set<string>>(new Set());
  const [showGroupModal, setShowGroupModal] = useState(false);

  // Flatten runs → one row per test
  const allRows = useMemo<DisplayRow[]>(() => {
    if (!runs) return [];
    const rows: DisplayRow[] = [];
    for (const run of runs) {
      for (const test of run.tests) {
        const passedSteps = test.passedSteps ?? test.steps.filter(s => s.status === 'ok').length;
        const durationMs  = Math.max(0, (test.finishedAt ?? run.runAt) - (test.startedAt ?? run.runAt));
        rows.push({
          key:         `${run.id}__${test.testId}`,
          runId:       run.id,
          testId:      test.testId,
          date:        test.startedAt || run.runAt,
          testName:    test.testName,
          tags:        test.tags ?? [],
          browser:     run.browser ?? 'chromium',
          status:      test.status,
          passedSteps,
          totalSteps:  test.steps.length,
          healCount:   test.healCount ?? 0,
          durationMs,
          groupId:     run.groupId,
          steps:       test.steps,
        });
      }
    }
    return rows;
  }, [runs]);

  // Stats
  const stats = useMemo(() => ({
    total:  allRows.length,
    passed: allRows.filter(r => r.status === 'passed').length,
    failed: allRows.filter(r => r.status === 'failed').length,
  }), [allRows]);

  // Filter + sort
  const filteredRows = useMemo<DisplayRow[]>(() => {
    let rows = allRows;

    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(r =>
        r.testName.toLowerCase().includes(q) ||
        r.tags.some(t => t.toLowerCase().includes(q))
      );
    }
    if (statusFilter !== 'all') {
      rows = rows.filter(r => r.status === statusFilter);
    }
    if (dateFrom) {
      const fromMs = new Date(dateFrom).getTime();
      rows = rows.filter(r => r.date >= fromMs);
    }
    if (dateTo) {
      const toMs = new Date(dateTo).getTime() + 86_400_000;
      rows = rows.filter(r => r.date <= toMs);
    }

    return [...rows].sort((a, b) => {
      let cmp = 0;
      if (sortCol === 'date')     cmp = a.date - b.date;
      if (sortCol === 'name')     cmp = a.testName.localeCompare(b.testName);
      if (sortCol === 'status')   cmp = a.status.localeCompare(b.status);
      if (sortCol === 'steps')    cmp = a.totalSteps - b.totalSteps;
      if (sortCol === 'duration') cmp = a.durationMs - b.durationMs;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allRows, search, statusFilter, dateFrom, dateTo, sortCol, sortDir]);

  // Sort click handler
  const handleSort = useCallback((col: SortCol) => {
    setSortCol(prev => {
      if (prev === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); return col; }
      setSortDir('desc');
      return col;
    });
  }, []);

  // Selection
  const toggleSelect = (key: string) => setSelected(prev => {
    const n = new Set(prev);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });
  const toggleAll = () => {
    if (selected.size === filteredRows.length) { setSelected(new Set()); }
    else { setSelected(new Set(filteredRows.map(r => r.key))); }
  };
  const allSelected = filteredRows.length > 0 && selected.size === filteredRows.length;

  // Expand
  const toggleExpand = (key: string) => setExpanded(prev => {
    const n = new Set(prev);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });

  // Delete selected
  const handleDeleteSelected = async () => {
    const runIds = new Set<string>();
    for (const key of selected) {
      const runId = key.split('__')[0];
      if (runId) runIds.add(runId);
    }
    if (!confirm(`Delete ${runIds.size} run record(s)? This removes all tests within those runs.`)) return;
    await db.runs.bulkDelete([...runIds]);
    setSelected(new Set());
  };

  // Export all
  const handleExport = () => {
    if (!runs) return;
    downloadJson(`plex-reports-${Date.now()}.json`, runs);
  };

  // HTML report download for one test row
  const handleDownloadHtml = (row: DisplayRow) => {
    const runResult: StudioRunResult = {
      testName:   row.testName,
      startedAt:  row.date,
      finishedAt: row.date + row.durationMs,
      steps: row.steps.map(s => ({
        stepId:    s.stepId,
        stepType:  s.stepType,
        status:    s.status,
        matchedBy: s.matchedBy,
        durationMs: s.durationMs,
        detail:    s.hint,
      })),
    };
    const html = generateHTMLReport([runResult]);
    downloadHtml(`${row.testName.replace(/\s+/g, '-')}-report.html`, html);
  };

  // Move to group confirm
  const handleGroupConfirm = async (groupId: string | null, newGroupName?: string) => {
    let targetGroupId: string | null = groupId;

    if (newGroupName) {
      const newGroup: DBRunGroup = {
        id:        crypto.randomUUID(),
        name:      newGroupName,
        createdAt: Date.now(),
      };
      await db.groups.add(newGroup);
      targetGroupId = newGroup.id;
    }

    // Collect unique run IDs from selection
    const runIds = new Set<string>();
    for (const key of selected) {
      const runId = key.split('__')[0];
      if (runId) runIds.add(runId);
    }

    await Promise.all([...runIds].map(id =>
      db.runs.update(id, { groupId: targetGroupId ?? undefined })
    ));

    setShowGroupModal(false);
    setSelected(new Set());
  };

  if (!runs) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#52525b', fontSize: 13 }}>
        Loading…
      </div>
    );
  }

  const S: React.CSSProperties = { flexShrink: 0 };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#0f0f11' }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 20px', borderBottom: '1px solid #27272a', flexShrink: 0,
      }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#e4e4e7' }}>Reports</h2>

        {/* Stats badges */}
        <span style={{ padding: '2px 9px', borderRadius: 12, background: '#27272a', color: '#a1a1aa', fontSize: 12, fontWeight: 600 }}>
          {stats.total} total
        </span>
        {stats.passed > 0 && (
          <span style={{ padding: '2px 9px', borderRadius: 12, background: '#14532d33', color: '#4ade80', fontSize: 12, fontWeight: 600 }}>
            ✓ {stats.passed}
          </span>
        )}
        {stats.failed > 0 && (
          <span style={{ padding: '2px 9px', borderRadius: 12, background: '#7f1d1d33', color: '#f87171', fontSize: 12, fontWeight: 600 }}>
            ✗ {stats.failed}
          </span>
        )}

        <div style={{ flex: 1 }} />

        {/* Export button */}
        {runs.length > 0 && (
          <button
            onClick={handleExport}
            style={{
              background: 'none', border: '1px solid #3f3f46', borderRadius: 6,
              padding: '6px 12px', color: '#a1a1aa', cursor: 'pointer', fontSize: 12,
              display: 'flex', alignItems: 'center', gap: 5,
            }}
          >
            ↓ Export Reports ({stats.total})
          </button>
        )}
      </div>

      {/* ── Filter row ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 20px', borderBottom: '1px solid #1c1c1f', flexShrink: 0,
      }}>
        {/* Search */}
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search test name or tag…"
          style={{
            background: '#18181b', border: '1px solid #27272a', borderRadius: 6,
            padding: '6px 10px', color: '#e4e4e7', fontSize: 12,
            width: 220, outline: 'none',
          }}
        />

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as StatusFilter)}
          style={{
            background: '#18181b', border: '1px solid #27272a', borderRadius: 6,
            padding: '6px 8px', color: '#a1a1aa', fontSize: 12, cursor: 'pointer',
          }}
        >
          <option value="all">All statuses</option>
          <option value="passed">Passed</option>
          <option value="failed">Failed</option>
        </select>

        {/* Date from */}
        <input
          type="date"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          style={{
            background: '#18181b', border: '1px solid #27272a', borderRadius: 6,
            padding: '5px 8px', color: '#a1a1aa', fontSize: 12, cursor: 'pointer',
            colorScheme: 'dark',
          }}
        />
        <span style={{ color: '#3f3f46', fontSize: 12 }}>—</span>
        <input
          type="date"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          style={{
            background: '#18181b', border: '1px solid #27272a', borderRadius: 6,
            padding: '5px 8px', color: '#a1a1aa', fontSize: 12, cursor: 'pointer',
            colorScheme: 'dark',
          }}
        />

        <div style={{ flex: 1 }} />

        {/* Results count */}
        <span style={{ color: '#52525b', fontSize: 12, ...S }}>
          {filteredRows.length} result{filteredRows.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Bulk actions bar ── */}
      {selected.size > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 20px', background: '#18181b', borderBottom: '1px solid #27272a',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 12, color: '#a1a1aa', fontWeight: 600 }}>
            {selected.size} selected
          </span>
          <button
            onClick={() => setShowGroupModal(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: '#3f2d0a', border: '1px solid #78350f', borderRadius: 6,
              padding: '5px 12px', color: '#fbbf24', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}
          >
            📁 Move to Group
          </button>
          <button
            onClick={() => void handleDeleteSelected()}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: '#450a0a', border: '1px solid #7f1d1d', borderRadius: 6,
              padding: '5px 12px', color: '#f87171', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}
          >
            ✗ Delete Selected
          </button>
          <button
            onClick={() => setSelected(new Set())}
            style={{
              background: 'none', border: '1px solid #27272a', borderRadius: 6,
              padding: '5px 12px', color: '#71717a', cursor: 'pointer', fontSize: 12,
            }}
          >
            ✗ Clear Selection
          </button>
          <button
            onClick={toggleAll}
            style={{
              background: 'none', border: '1px solid #27272a', borderRadius: 6,
              padding: '5px 12px', color: '#71717a', cursor: 'pointer', fontSize: 12,
            }}
          >
            ☑ Select All ({filteredRows.length})
          </button>
        </div>
      )}

      {/* ── Table ── */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {filteredRows.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#3f3f46', fontSize: 13 }}>
            {runs.length === 0
              ? 'No runs yet. Select tests in Test Cases → Run Selected → Play to execute.'
              : 'No results match the current filters.'}
          </div>
        ) : (
          <>
            {/* Table header row */}
            <div style={{
              display: 'grid', gridTemplateColumns: COL,
              background: '#111113', borderBottom: '1px solid #27272a',
              position: 'sticky', top: 0, zIndex: 10,
            }}>
              {/* Checkbox — select all */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 0' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  style={{ cursor: 'pointer', accentColor: '#7c3aed' }}
                />
              </div>
              {(
                [
                  { label: 'Date',     col: 'date'     as SortCol },
                  { label: 'Test Name', col: 'name'    as SortCol },
                  { label: 'Tags',      col: null },
                  { label: 'Browser',   col: null },
                  { label: 'Status',    col: 'status'  as SortCol },
                  { label: 'Steps',     col: 'steps'   as SortCol },
                  { label: 'Duration',  col: 'duration' as SortCol },
                  { label: 'Report',    col: null },
                ] as { label: string; col: SortCol | null }[]
              ).map(({ label, col }) => (
                <div
                  key={label}
                  onClick={() => col && handleSort(col)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '8px 8px', fontSize: 11, fontWeight: 700,
                    color: '#52525b', letterSpacing: '0.06em', textTransform: 'uppercase',
                    cursor: col ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                >
                  {label}
                  {col && <SortIcon col={col} active={sortCol === col} dir={sortDir} />}
                </div>
              ))}
            </div>

            {/* Data rows */}
            {filteredRows.map(row => {
              const isSelected = selected.has(row.key);
              const isExpanded = expanded.has(row.key);
              const stepsLabel = `${row.passedSteps}/${row.totalSteps}${row.healCount > 0 ? ` X${row.healCount}` : ''}`;

              return (
                <div
                  key={row.key}
                  style={{
                    borderBottom: '1px solid #1c1c1f',
                    borderLeft: isSelected ? '3px solid #ef4444' : '3px solid transparent',
                    background: isSelected ? '#18100f' : 'transparent',
                  }}
                >
                  {/* Main row */}
                  <div
                    style={{
                      display: 'grid', gridTemplateColumns: COL,
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => toggleExpand(row.key)}
                  >
                    {/* Checkbox */}
                    <div
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 0' }}
                      onClick={e => { e.stopPropagation(); toggleSelect(row.key); }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(row.key)}
                        onClick={e => e.stopPropagation()}
                        style={{ cursor: 'pointer', accentColor: '#7c3aed' }}
                      />
                    </div>

                    {/* Date */}
                    <div style={{ padding: '10px 8px', fontSize: 12, color: '#71717a' }}>
                      {fmtDate(row.date)}
                    </div>

                    {/* Test Name */}
                    <div style={{
                      padding: '10px 8px', fontSize: 13, fontWeight: 500, color: '#e4e4e7',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {row.testName}
                    </div>

                    {/* Tags */}
                    <div style={{ padding: '10px 8px', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {row.tags.slice(0, 3).map(tag => (
                        <span
                          key={tag}
                          style={{
                            padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                            background: '#1e1b4b', color: '#818cf8',
                          }}
                        >{tag}</span>
                      ))}
                    </div>

                    {/* Browser */}
                    <div style={{ padding: '10px 8px', fontSize: 12, color: '#71717a' }}>
                      {browserLabel(row.browser)}
                    </div>

                    {/* Status */}
                    <div style={{ padding: '10px 8px' }}>
                      <StatusBadge status={row.status} healCount={row.healCount} />
                    </div>

                    {/* Steps */}
                    <div style={{ padding: '10px 8px', fontSize: 12, color: '#71717a' }}>
                      {stepsLabel}
                    </div>

                    {/* Duration */}
                    <div style={{ padding: '10px 8px', fontSize: 12, color: '#71717a' }}>
                      {fmtDuration(row.durationMs)}
                    </div>

                    {/* Report */}
                    <div style={{ padding: '10px 8px' }}>
                      <button
                        onClick={e => { e.stopPropagation(); handleDownloadHtml(row); }}
                        style={{
                          background: 'none', border: 'none', padding: 0,
                          color: '#a78bfa', cursor: 'pointer', fontSize: 12,
                          textDecoration: 'underline', textDecorationStyle: 'dotted',
                        }}
                      >↓ HTML</button>
                    </div>
                  </div>

                  {/* Expanded step sub-table */}
                  {isExpanded && <StepSubTable steps={row.steps} />}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* ── Move to Group Modal ── */}
      {showGroupModal && (
        <MoveToGroupModal
          selectedCount={(() => {
            const ids = new Set<string>();
            for (const k of selected) ids.add(k.split('__')[0]);
            return ids.size;
          })()}
          groups={groups}
          onConfirm={(groupId, newGroupName) => void handleGroupConfirm(groupId, newGroupName)}
          onCancel={() => setShowGroupModal(false)}
        />
      )}
    </div>
  );
}
